import { userDashboardLinks } from '../../routes/navConfig.jsx';

const walk = (nodes, targetPath, ancestors = []) => {
    for (const node of nodes || []) {
        if (node.path && node.path === targetPath) {
            return { node, ancestors };
        }
        if (node.subItems?.length) {
            const found = walk(node.subItems, targetPath, [...ancestors, node]);
            if (found) return found;
        }
    }
    return null;
};

const findByPrefix = (nodes, pathname, ancestors = []) => {
    let best = null;
    for (const node of nodes || []) {
        if (node.path && (pathname === node.path || pathname.startsWith(node.path + '/'))) {
            if (!best || node.path.length > best.node.path.length) {
                best = { node, ancestors };
            }
        }
        if (node.subItems?.length) {
            const childBest = findByPrefix(node.subItems, pathname, [...ancestors, node]);
            if (childBest && (!best || childBest.node.path.length > best.node.path.length)) {
                best = childBest;
            }
        }
    }
    return best;
};

export const resolveMenuContext = (pagePath, navTree = userDashboardLinks) => {
    const exact = walk(navTree, pagePath);
    const result = exact || findByPrefix(navTree, pagePath);
    if (!result) return null;

    const { node, ancestors } = result;
    const parent = ancestors[ancestors.length - 1] || null;
    const grandparent = ancestors[ancestors.length - 2] || null;

    // The "scope" — bottom-pinned (Profile / Settings / Dev Tools / Help,
    // reached via the avatar popup) vs main workspace. Peers always come
    // from the same scope so the avatar group and the workspace group don't
    // mingle in the dropdown.
    const topLevel = ancestors[0] || node;
    const inBottom = Boolean(topLevel.bottom);

    if (!parent) {
        // Top-level node — its peers are the other top-level nodes in the
        // same scope. Include self so the current page renders with a check.
        return {
            currentNode: node,
            parentLabel: inBottom ? 'Account' : 'Sections',
            grandparentLabel: '',
            sisters: navTree.filter(s => Boolean(s.bottom) === inBottom),
            parentSisters: [],
        };
    }

    const sisters = parent.subItems || [];
    const parentSisters = grandparent?.subItems
        ? grandparent.subItems.filter(s => s !== parent)
        : navTree.filter(s => s !== parent && Boolean(s.bottom) === inBottom);

    return {
        currentNode: node,
        parentLabel: parent.label || '',
        grandparentLabel: grandparent?.label || '',
        sisters,
        parentSisters,
    };
};

/**
 * The menu for a page: its own nav entry (`pagePath`) when the tree has it, else the entry for the URL it is on.
 * A page can name a path outside the view it renders in - a hub path like '/game' or '/portfolio', or a URL from
 * before the game pages moved under /game/<slug> - and the URL still places it.
 */
export const resolvePageMenu = (pagePath, pathname, navTree) =>
    (pagePath && resolveMenuContext(pagePath, navTree))
    || (pathname && pathname !== pagePath && resolveMenuContext(pathname, navTree))
    || null;

export const getFirstLeafPath = (node) => {
    if (!node) return null;
    if (node.path && (!node.subItems || node.subItems.length === 0)) return node.path;
    if (node.subItems?.length) return getFirstLeafPath(node.subItems[0]);
    return node.path || null;
};

// Onboarding steps past the furthest one reached, and "No games yet"-style rows: listed, but go nowhere.
export const isInertMenuItem = (item) => Boolean(item?.isLocked || item?.isPlaceholder);

// Where picking a row goes. A row with a page of its own opens it (Games -> the games list, Flywheels -> the
// flywheel list), as in the sidebar; a group without one opens its first leaf.
export const menuPickPath = (item) => (isInertMenuItem(item) ? null : (item?.path || getFirstLeafPath(item)));
